// Design tokens for the modular chat workspace.
//
// This is a superset drop-in for the old local `workspacePalette` object: every
// flat surface/border/text/accent key the workspace already referenced is kept,
// plus the new system (elevation, radii, shadows, typography, tabular numerals).
// Consumed via MUI `sx` only -- intentionally NOT a global ThemeProvider override,
// so the rest of the app (e.g. /chat) is untouched.
import { isDarkMode } from "../../utils/constants";

const dark = isDarkMode;

export const workspaceTokens = {
  // ---- Surfaces: neutral Incite layers shared with the core chat product ----
  page: dark ? "#101114" : "#F5F7FA",
  leftRail: dark ? "#121316" : "#FFFFFF",
  center: dark ? "#111318" : "#F7F8FA",
  panel: dark ? "#17191E" : "#FFFFFF",
  panelSoft: dark ? "#1D2026" : "#F1F3F6",
  raised: dark ? "#242832" : "#FFFFFF",
  field: dark ? "#14161B" : "#F7F8FA",

  // ---- Borders ----
  border: dark ? "rgba(235,240,250,0.09)" : "rgba(17,24,39,0.10)",
  strongBorder: dark ? "rgba(235,240,250,0.17)" : "rgba(17,24,39,0.18)",

  // ---- Text ----
  text: dark ? "#F5F7FA" : "#121417",
  muted: dark ? "rgba(245,247,250,0.67)" : "rgba(18,20,23,0.64)",
  subtle: dark ? "rgba(245,247,250,0.43)" : "rgba(18,20,23,0.44)",

  // ---- Incite accent / semantic ----
  accent: dark ? "#7AA2FF" : "#2563EB",
  accentHover: dark ? "#91B3FF" : "#1D4ED8",
  accentText: dark ? "#A8C1FF" : "#1D4ED8",
  onAccent: "#FFFFFF",
  accentSoft: dark ? "rgba(122,162,255,0.14)" : "rgba(37,99,235,0.09)",
  accentBorder: dark ? "rgba(122,162,255,0.32)" : "rgba(37,99,235,0.25)",
  sentMessage: dark ? "#252C3E" : "#E8EEFA",
  attentionGradient: dark
    ? "linear-gradient(145deg, rgba(122,162,255,0.12) 0%, rgba(29,32,38,0.96) 52%, rgba(23,25,30,1) 100%)"
    : "linear-gradient(145deg, rgba(37,99,235,0.08) 0%, rgba(255,255,255,0.98) 58%, rgba(241,243,246,0.94) 100%)",
  green: dark ? "#79B98C" : "#2E7D4F",
  greenSoft: dark ? "rgba(121,185,140,0.13)" : "rgba(46,125,79,0.09)",
  greenBorder: dark ? "rgba(121,185,140,0.30)" : "rgba(46,125,79,0.24)",
  warning: dark ? "#E2AE63" : "#9A6119",
  red: dark ? "#E87972" : "#B9433D",

  // ---- Elevation extras ----
  // 1px inner top highlight gives the "machined panel" feel referenced in the plan.
  innerHighlight: dark
    ? "inset 0 1px 0 rgba(255,255,255,0.045)"
    : "inset 0 1px 0 rgba(255,255,255,0.75)",
  shadows: {
    card: dark ? "0 1px 2px rgba(0,0,0,0.42)" : "0 1px 2px rgba(52,45,34,0.07)",
    cardHover: dark ? "0 12px 30px rgba(0,0,0,0.42)" : "0 12px 30px rgba(52,45,34,0.11)",
    raised: dark ? "0 20px 48px rgba(0,0,0,0.54)" : "0 20px 48px rgba(52,45,34,0.16)",
  },

  // ---- Radii scale (replaces the 8/12/18/999 sprawl) ----
  radii: { sm: 8, md: 12, lg: 16, pill: 999 },

  // ---- Typography ----
  fontFamily: {
    ui: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    numeric: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    editorial: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  // Restrained weight scale -- hierarchy comes from size + weight + color, not weight alone.
  weights: { regular: 400, medium: 500, semibold: 600, bold: 700 },
  sizes: {
    microLabel: 11.5,
    secondary: 13,
    body: 14.5,
    title: 16.5,
    priceLg: 22,
    priceXl: 26,
  },
  letterSpacing: { label: "0.06em" },

  // Spread onto any price / % / metric Typography so numeric columns align and
  // do not jitter as values tick -- the single highest-impact terminal detail.
  tnum: { fontVariantNumeric: "tabular-nums", fontFeatureSettings: '"tnum" 1' },
};

export default workspaceTokens;
